import React, { useState } from 'react'
import Card from 'components/common/cards/Card'
import styled from 'styled-components'
import Text from 'components/common/Text'
import { FaFacebookF, FaHome, FaEnvelope, FaComment, FaFilePdf } from 'react-icons/fa'
import Button from 'components/common/Button'
import Popup from 'components/common/Popup'
import CopyToClipboard from 'components/common/CopyToClipboard'
import { useTenantProfile } from 'hooks/api/tenant/useTenantProfile'
import { origin } from 'utility/domain'

import colors from 'utility/colors'
import isBrowser from 'utility/isBrowser'

const ShareLink = styled.a`
	margin-bottom: 2px;
	color: ${colors.GREEN};
	display: inline;
	padding-bottom: 5px;
	font-size: 18px;
	font-weight: bold;
	:hover {
		color: ${colors.GREEN};
	}
`

const CopyToClipBoardBox = styled.div`
	border-style: dashed;
	border-width: 3px;
	border-radius: 5px;
	padding: 10px;
	margin-top: 20px;
`

const CopyToClipboardBlock = ({ children, text, onCopy }) => {
	return (
		<CopyToClipboard onCopy={onCopy} text={text}>
			<CopyToClipBoardBox>
				<Text>{children}</Text>
			</CopyToClipBoardBox>
		</CopyToClipboard>
	)
}

const SocialButton = ({
	id,
	Icon,
	onClick,
	background = '#ffcc00',
	color = colors.WHITE,
	link,
	withPopup,
	children,
}) => {
	let mainButton = (
		<Button
			id={id}
			onClick={onClick}
			style={{
				width: 'auto',
				height: 'auto',
				marginRight: 10,
				marginBottom: 10,
				paddingLeft: 10,
				paddingRight: 10,
				paddingTop: 6,
				paddingBottom: 6,
				backgroundColor: background,
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'center',
			}}
		>
			<Icon size={14} color={color} style={{ marginTop: 3 }} />
			<Text
				size={16}
				style={{ paddingLeft: 6, paddingBottom: 0, marginBottom: 0, top: 0 }}
				color={color}
			>
				{children}
			</Text>
		</Button>
	)

	if (link && !withPopup) {
		mainButton = (
			<a href={link} target={'_blank'}>
				{mainButton}
			</a>
		)
	}

	return mainButton
}

const SocialButtons = ({
	withPopup,
	url = `${origin}/partners/friends-and-family`,
	mainCopy,
	facebook = true,
	nextdoor = false,
	email = false,
	text = false,
	flyer = false,
	agentUrl,
}) => {
	const { data: tenantProfile } = useTenantProfile()
	const tenantName = tenantProfile?.name

	if (!mainCopy) {
		mainCopy = `I signed up for ${tenantName} to save money on my property taxes, and you should too!`
	}

	const [popup, setPopup] = useState(false)
	const [copied, setCopied] = useState(false)

	const textCopy = `Hi! I wanted to share some exciting news... to help my clients save time and money on their property taxes, I’ve partnered with a fantastic new company called ${tenantName} - their AI-technology and local, experienced professionals deliver the most effective and affordable way to make sure you get a fair property tax value each year. Visit my site to see what you can save and use my partner discount on ${tenantName}. Call me if you have any questions. :) ${agentUrl}`
	const emailCopy = `Hello! I recently learned that 92% of Texas homeowners do not have a real estate professional helping them with property taxes! Some homeowners try to protest on their own but it takes a lot of time sifting through real estate data, filing paperwork with the county, and attending meetings (often more than one) with a county appraiser.

As your trusted real estate professional, I would like to help you with your property taxes this year. To do that I’ve partnered with a fantastic new company called ${tenantName}. Their AI-technology and local, experienced professionals deliver the most effective and affordable way to make sure you get a fair property tax value each year.
  
Visit my site ${agentUrl} to see what you can save and use my partner discount on ${tenantName}. Feel free to share my site with your friends and neighbors too!
  
Thanks for trusting me for all your real estate needs, and as always please give me a call if there’s anything I can help with.`

	const socialCopy = `Hi friends! I recently learned that 92% of Texas homeowners do not have a real estate professional helping them with property taxes. As your trusted real estate professional, I would like to help and have partnered with a fantastic new company called ${tenantName}. Their AI-technology and local, experienced professionals deliver the most effective and affordable way to make sure you get a fair property tax value each year.

Signup here ${agentUrl} to see what you can save and use my partner discount. It’s a no-brainer, as they typically save homeowners hundreds and often thousands of dollars each year. Feel free to share my site with your friends and neighbors too!`
	return (
		<>
			<div
				style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}
			>
				{facebook && (
					<SocialButton
						id={'FacebookShare'}
						Icon={FaFacebookF}
						onClick={() => {
							return withPopup
								? setPopup('facebook')
								: isBrowser
								? window.open(
										'https://www.facebook.com/sharer/sharer.php?u=' + (agentUrl || url),
										'facebook-share-dialog',
										'width=626, height=436',
								  )
								: null
						}}
						withPopup={withPopup}
						background={'#3B5998'}
					>
						Facebook
					</SocialButton>
				)}
				{nextdoor && (
					<SocialButton
						id={'NextdoorShare'}
						Icon={FaHome}
						onClick={() => {
							return withPopup ? setPopup('nextdoor') : null
						}}
						withPopup={withPopup}
						link='https://nextdoor.com'
						background={'#99CC3F'}
					>
						Nextdoor
					</SocialButton>
				)}
				{email && (
					<SocialButton
						id={'EmailShare'}
						Icon={FaEnvelope}
						onClick={() => {
							return withPopup ? setPopup('email') : null
						}}
						withPopup={withPopup}
						color={colors.WHITE}
						link={`mailto:?subject=Checkout ${tenantName}&body=${mainCopy ? mainCopy : ''}${
							agentUrl || url
						}`}
						background={colors.GRAY}
					>
						Email
					</SocialButton>
				)}
				{text && (
					<SocialButton
						id={'SMSShare'}
						Icon={FaComment}
						onClick={() => {
							return withPopup ? setPopup('text') : null
						}}
						withPopup={withPopup}
						color={colors.WHITE}
						link={`sms:?&body=${mainCopy || ''} ${agentUrl || url}`}
						background={colors.GRAY}
					>
						Text
					</SocialButton>
				)}
				{flyer && (
					<SocialButton
						id={'FlyerShare'}
						Icon={FaFilePdf}
						link={'https://wp.hometaxshield.com/wp-content/uploads/2021/04/Why-Choose-HTS.pdf'}
						background={colors.GRAY}
					>
						Download Flyer
					</SocialButton>
				)}
			</div>

			{popup === 'facebook' ? (
				<Popup
					onClose={() => {
						setCopied(false)
						setPopup(null)
					}}
					initialOpen={true}
					title={'Share on Facebook!'}
				>
					<>
						<CopyToClipboardBlock onCopy={() => setCopied(true)} text={socialCopy}>
							Hi friends! I recently learned that 92% of Texas homeowners do not have a real estate
							professional helping them with property taxes. As your trusted real estate
							professional, I would like to help and have partnered with a fantastic new company
							called {tenantName}. Their AI-technology and local, experienced professionals deliver
							the most effective and affordable way to make sure you get a fair property tax value
							each year.
							<br />
							<br />
							Signup here{' '}
							<ShareLink target='_blank' href={agentUrl}>
								{agentUrl}
							</ShareLink>{' '}
							to see what you can save and use my partner discount. It’s a no-brainer, as they
							typically save homeowners hundreds and often thousands of dollars each year. Feel free
							to share my site with your friends and neighbors too!
						</CopyToClipboardBlock>
						<Text size={14} style={{ width: '100%', textAlign: 'right' }}>
							{copied ? 'Copied to clipboard!' : 'Click box to copy to clipboard'}
						</Text>
						<SocialButton
							id={'FBShareModal'}
							Icon={FaFacebookF}
							onClick={() => {
								return isBrowser
									? window.open(
											'https://www.facebook.com/sharer/sharer.php?u=' + url,
											'facebook-share-dialog',
											'width=626, height=436',
									  )
									: null
							}}
							background={'#3B5998'}
						>
							Continue to Facebook
						</SocialButton>
					</>
				</Popup>
			) : null}

			{popup === 'nextdoor' ? (
				<Popup
					onClose={() => {
						setCopied(false)
						setPopup(null)
					}}
					initialOpen={true}
					title={'Share on Nextdoor!'}
				>
					<>
						<CopyToClipboardBlock onCopy={() => setCopied(true)} text={socialCopy}>
							Hi friends! I recently learned that 92% of Texas homeowners do not have a real estate
							professional helping them with property taxes. As your trusted real estate
							professional, I would like to help and have partnered with a fantastic new company
							called {tenantName}. Their AI-technology and local, experienced professionals deliver
							the most effective and affordable way to make sure you get a fair property tax value
							each year.
							<br />
							<br />
							Signup here{' '}
							<ShareLink target='_blank' href={agentUrl}>
								{agentUrl}
							</ShareLink>{' '}
							to see what you can save and use my partner discount. It’s a no-brainer, as they
							typically save homeowners hundreds and often thousands of dollars each year. Feel free
							to share my site with your friends and neighbors too!
						</CopyToClipboardBlock>
						<Text size={14} style={{ width: '100%', textAlign: 'right' }}>
							{copied ? 'Copied to clipboard!' : 'Click box to copy to clipboard'}
						</Text>
					</>
				</Popup>
			) : null}

			{popup === 'email' ? (
				<Popup
					onClose={() => {
						setCopied(false)
						setPopup(null)
					}}
					initialOpen={true}
					title={'Share via Email!'}
				>
					<>
						<CopyToClipboardBlock onCopy={() => setCopied(true)} text={emailCopy}>
							Hello! I recently learned that 92% of Texas homeowners do not have a real estate
							professional helping them with property taxes! Some homeowners try to protest on their
							own but it takes a lot of time sifting through real estate data, filing paperwork with
							the county, and attending meetings (often more than one) with a county appraiser.
							<br />
							<br />
							As your trusted real estate professional, I would like to help you with your property
							taxes this year. To do that I’ve partnered with a fantastic new company called Home
							Tax Shield. Their AI-technology and local, experienced professionals deliver the most
							effective and affordable way to make sure you get a fair property tax value each year.
							<br />
							<br />
							Visit my site{' '}
							<ShareLink target='_blank' href={agentUrl}>
								{agentUrl}
							</ShareLink>{' '}
							to see what you can save and use my partner discount on {tenantName}. Feel free to
							share my site with your friends and neighbors too!
							<br />
							<br />
							Thanks for trusting me for all your real estate needs, and as always please give me a
							call if there’s anything I can help with.
						</CopyToClipboardBlock>
						<Text size={14} style={{ width: '100%', textAlign: 'right' }}>
							{copied ? 'Copied to clipboard!' : 'Click box to copy to clipboard'}
						</Text>
						<SocialButton
							id={'EmailShareModal'}
							Icon={FaEnvelope}
							color={'#ffffff'}
							link={`mailto:?subject=&body=${emailCopy}`}
							background={colors.GRAY}
						>
							Open Email
						</SocialButton>
					</>
				</Popup>
			) : null}

			{popup === 'text' ? (
				<Popup
					onClose={() => {
						setCopied(false)
						setPopup(null)
					}}
					initialOpen={true}
					title={'Share via Text!'}
				>
					<>
						<CopyToClipboardBlock onCopy={() => setCopied(true)} text={textCopy}>
							Hi! I wanted to share some exciting news... to help my clients save time and money on
							their property taxes, I’ve partnered with a fantastic new company called {tenantName}{' '}
							- their AI-technology and local, experienced professionals deliver the most effective
							and affordable way to make sure you get a fair property tax value each year. Visit my
							site to see what you can save and use my partner discount on {tenantName}. Call me if
							you have any questions. :){' '}
							<ShareLink target='_blank' href={agentUrl}>
								{agentUrl}
							</ShareLink>
						</CopyToClipboardBlock>
						<Text size={14} style={{ width: '100%', textAlign: 'right' }}>
							{copied ? 'Copied to clipboard!' : 'Click box to copy to clipboard'}
						</Text>
						<SocialButton
							id={'SMSShareModal'}
							Icon={FaComment}
							color={'#ffffff'}
							link={`sms:?&body=${textCopy}`}
							background={colors.GRAY}
						>
							Continue & Send Text
						</SocialButton>
					</>
				</Popup>
			) : null}
		</>
	)
}

export default SocialButtons
